import * as i0 from '@angular/core';
import { inject, EventEmitter, forwardRef, Component, Input, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@itero/ui-toolkit-angular/core';
import { TlkIsDisabledDirective, NgControlDirective } from '@itero/ui-toolkit-angular/core';

let nextUniqueId = 0;
class TlkCheckboxComponent {
    constructor() {
        /** The unique id of the element */
        this.id = `tlk-checkbox-${nextUniqueId++}`;
        /**
         * @prop
         * @ignore
         */
        this._touched = false;
        /**
         * @prop
         * @ignore
         */
        this._checked = false;
        /**
         * @prop
         * @ignore
         */
        this._indeterminate = false;
        /**
         * @prop
         * @ignore
         */
        this.isDisabledDirective = inject(TlkIsDisabledDirective, { self: true });
        this.changeEvent = new EventEmitter();
        this.indeterminateChanged = new EventEmitter();
        /**
         * @prop
         * @ignore
         */
        this.propagateChange = () => undefined;
        /**
         * @prop
         * @ignore
         */
        this.propagateTouched = () => undefined;
    }
    /**
     * @prop
     * @ignore
     */
    get _inputId() {
        return this.id + '-input';
    }
    /**
     * @prop
     * @ignore
     */
    get _labelId() {
        return this.id + '-label';
    }
    /**
     * input where you can pass desirable state of checkbox "true" or "false".
     * Can be helpful when you don't use formControl, otherwise you can patchValue of formControl
     * */
    set checked(value) {
        this._checked = value;
        this.indeterminate = false;
    }
    get checked() {
        return this._checked;
    }
    set indeterminate(value) {
        const updateCheckedState = (newIndeterminateState) => {
            if (newIndeterminateState) {
                this._checked = false;
            }
        };
        this.setIndeterminateInternal(value, updateCheckedState);
    }
    get indeterminate() {
        return this._indeterminate;
    }
    get disabled() {
        return this.isDisabledDirective.isDisabled;
    }
    /**
     * @method
     * @ignore
     */
    _blurHandler() {
        if (this.disabled) {
            return;
        }
        this.markAsTouched();
    }
    /**
     * @method
     * @ignore
     */
    setIndeterminateInternal(value, cb) {
        const changed = value !== this._indeterminate;
        this._indeterminate = value;
        if (changed) {
            if (cb) {
                cb(this._indeterminate);
            }
            this.indeterminateChanged.emit(this._indeterminate);
        }
    }
    /**
     * @method
     * @ignore
     */
    markAsTouched() {
        if (!this._touched) {
            this.propagateTouched();
            this._touched = true;
        }
    }
    /**
     * @method
     * @ignore
     */
    _changeHandler(event) {
        if (this.disabled) {
            return;
        }
        this._checked = !this.checked;
        this.setIndeterminateInternal(false);
        this.markAsTouched();
        this.propagateChange(this._checked);
        this.changeEvent.emit({
            event,
            checked: this._checked
        });
    }
    writeValue(val) {
        this._checked = val;
        this.setIndeterminateInternal(false);
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.isDisabledDirective.setDisabledState(isDisabled);
    }
}
TlkCheckboxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkCheckboxComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TlkCheckboxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: TlkCheckboxComponent, isStandalone: true, selector: "tlk-checkbox", inputs: { id: "id", checked: "checked", indeterminate: "indeterminate" }, outputs: { changeEvent: "changeEvent", indeterminateChanged: "indeterminateChanged" }, host: { properties: { "class.tlk-checkbox_checked": "checked", "class.tlk-checkbox_indeterminate": "indeterminate", "class.tlk-checkbox_disabled": "disabled", "attr.id": "id", "attr.data-testid": "id" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TlkCheckboxComponent),
            multi: true
        }
    ], hostDirectives: [{ directive: i1.NgControlDirective }, { directive: i1.TlkIsDisabledDirective, inputs: ["isDisabled", "isDisabled"], outputs: ["disableChangeEvent", "disableChangeEvent"] }], ngImport: i0, template: "<label [for]=\"_inputId\" [attr.data-testid]=\"_labelId\" [attr.id]=\"_labelId\">\n\t<div class=\"tlk-checkbox__trigger\" [attr.aria-checked]=\"checked\">\n\t\t<input\n\t\t\t[attr.id]=\"_inputId\"\n            [attr.data-testid]=\"_inputId\"\n\t\t\tclass=\"tlk-checkbox__input_hidden\"\n\t\t\t[disabled]=\"disabled\"\n\t\t\t(change)=\"_changeHandler($event)\"\n\t\t\t(blur)=\"_blurHandler()\"\n\t\t\t[checked]=\"checked\"\n\t\t\t[indeterminate]=\"indeterminate\"\n\t\t\ttype=\"checkbox\"\n\t\t/>\n\t\t<div class=\"tlk-checkbox__tickbox\"></div>\n\t\t<div class=\"tlk-checkbox__tick\"></div>\n\t</div>\n\t<div class=\"tlk-checkbox__label-text\"\n\t\t><ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container\n\t></div>\n</label>\n\n<ng-template #contentTpl>\n\t<div>\n\t\t<ng-content></ng-content>\n\t</div>\n\t<div class=\"tlk-checkbox__label-desc\">\n\t\t<ng-content select=\"[description]\"></ng-content>\n\t</div>\n</ng-template>\n", styles: [":host,:host *,:host :before,:host :after{box-sizing:border-box}:host{display:block}label{display:flex;align-items:flex-start;cursor:pointer}:host.tlk-checkbox_disabled ::ng-deep label{pointer-events:none;cursor:default}label>::ng-deep *:not(:empty)+*:not(:empty){margin-left:8px}.tlk-checkbox__label-text{font:var(--tlk-text-body-1-font);line-height:28px;color:var(--tlk-gray-80)}:host.tlk-checkbox_disabled ::ng-deep .tlk-checkbox__label-text{color:var(--tlk-gray-50)}.tlk-checkbox__label-desc{font:var(--tlk-text-label-description-font)}.tlk-checkbox__input_hidden{border:0;clip:rect(0 0 0 0);position:absolute;overflow:hidden;width:1px;height:1px;outline:0;margin:-1px}.tlk-checkbox__trigger{position:relative;padding:0;height:28px;width:20px;display:flex;align-items:center;outline:none;border:none;background:transparent}.tlk-checkbox__tickbox{height:20px;width:20px;overflow:hidden;top:0;border-radius:var(--tlk-main-unit);border:1px solid var(--tlk-gray-60);background:var(--tlk-white)}.tlk-checkbox__tickbox:before{width:28px;height:28px;border-radius:calc(var(--tlk-main-unit) * 2);border:solid transparent 2px;position:absolute;display:block;content:\"\";top:calc(50% - 14px);left:calc(50% - 14px);transition:transform .1s ease-out;transform:scale(.7142857143)}.tlk-checkbox__input_hidden:focus+::ng-deep .tlk-checkbox__tickbox:before{transform:scale(1);transition:transform .1s ease-in;border-color:var(--tlk-blue-60)}.tlk-checkbox__tickbox:after{width:36px;height:36px;opacity:0;position:absolute;transition:opacity .2s ease-out,background-color .1s ease-out;display:block;content:\"\";top:calc(50% - 18px);left:calc(50% - 18px);border-radius:calc(var(--tlk-main-unit) * 2);border:9px solid var(--tlk-blue-60)}:host:not(.tlk-checkbox_disabled):hover ::ng-deep .tlk-checkbox__tickbox:after{opacity:.2;transition:opacity .2s ease-in,background-color .1s ease-in}.tlk-checkbox__tick{position:absolute;right:6.5px;top:4.0251262659px;z-index:1;display:inline-block;transform:rotate(45deg);width:7px;height:15px;border-bottom:3px solid;border-right:3px solid;border-color:transparent}:host.tlk-checkbox_checked ::ng-deep .tlk-checkbox__tick{border-color:var(--tlk-blue-80)}:host.tlk-checkbox_indeterminate ::ng-deep .tlk-checkbox__tick{transition:none;transform:none;border-right:none;border-bottom:4px solid var(--tlk-blue-80);height:4px;width:8px;right:6px;top:12px}:host.tlk-checkbox_checked.tlk-checkbox_disabled ::ng-deep .tlk-checkbox__tick{border-color:var(--tlk-gray-50)}:host.tlk-checkbox_indeterminate.tlk-checkbox_disabled ::ng-deep .tlk-checkbox__tick{border-color:var(--tlk-gray-50)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TlkCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tlk-checkbox', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TlkCheckboxComponent),
                            multi: true
                        }
                    ], imports: [CommonModule], hostDirectives: [
                        NgControlDirective,
                        {
                            directive: TlkIsDisabledDirective,
                            // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                            inputs: ['isDisabled'],
                            // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
                            outputs: ['disableChangeEvent']
                        }
                    ], host: {
                        '[class.tlk-checkbox_checked]': 'checked',
                        '[class.tlk-checkbox_indeterminate]': 'indeterminate',
                        '[class.tlk-checkbox_disabled]': 'disabled',
                        '[attr.id]': 'id',
                        '[attr.data-testid]': 'id'
                    }, template: "<label [for]=\"_inputId\" [attr.data-testid]=\"_labelId\" [attr.id]=\"_labelId\">\n\t<div class=\"tlk-checkbox__trigger\" [attr.aria-checked]=\"checked\">\n\t\t<input\n\t\t\t[attr.id]=\"_inputId\"\n            [attr.data-testid]=\"_inputId\"\n\t\t\tclass=\"tlk-checkbox__input_hidden\"\n\t\t\t[disabled]=\"disabled\"\n\t\t\t(change)=\"_changeHandler($event)\"\n\t\t\t(blur)=\"_blurHandler()\"\n\t\t\t[checked]=\"checked\"\n\t\t\t[indeterminate]=\"indeterminate\"\n\t\t\ttype=\"checkbox\"\n\t\t/>\n\t\t<div class=\"tlk-checkbox__tickbox\"></div>\n\t\t<div class=\"tlk-checkbox__tick\"></div>\n\t</div>\n\t<div class=\"tlk-checkbox__label-text\"\n\t\t><ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container\n\t></div>\n</label>\n\n<ng-template #contentTpl>\n\t<div>\n\t\t<ng-content></ng-content>\n\t</div>\n\t<div class=\"tlk-checkbox__label-desc\">\n\t\t<ng-content select=\"[description]\"></ng-content>\n\t</div>\n</ng-template>\n", styles: [":host,:host *,:host :before,:host :after{box-sizing:border-box}:host{display:block}label{display:flex;align-items:flex-start;cursor:pointer}:host.tlk-checkbox_disabled ::ng-deep label{pointer-events:none;cursor:default}label>::ng-deep *:not(:empty)+*:not(:empty){margin-left:8px}.tlk-checkbox__label-text{font:var(--tlk-text-body-1-font);line-height:28px;color:var(--tlk-gray-80)}:host.tlk-checkbox_disabled ::ng-deep .tlk-checkbox__label-text{color:var(--tlk-gray-50)}.tlk-checkbox__label-desc{font:var(--tlk-text-label-description-font)}.tlk-checkbox__input_hidden{border:0;clip:rect(0 0 0 0);position:absolute;overflow:hidden;width:1px;height:1px;outline:0;margin:-1px}.tlk-checkbox__trigger{position:relative;padding:0;height:28px;width:20px;display:flex;align-items:center;outline:none;border:none;background:transparent}.tlk-checkbox__tickbox{height:20px;width:20px;overflow:hidden;top:0;border-radius:var(--tlk-main-unit);border:1px solid var(--tlk-gray-60);background:var(--tlk-white)}.tlk-checkbox__tickbox:before{width:28px;height:28px;border-radius:calc(var(--tlk-main-unit) * 2);border:solid transparent 2px;position:absolute;display:block;content:\"\";top:calc(50% - 14px);left:calc(50% - 14px);transition:transform .1s ease-out;transform:scale(.7142857143)}.tlk-checkbox__input_hidden:focus+::ng-deep .tlk-checkbox__tickbox:before{transform:scale(1);transition:transform .1s ease-in;border-color:var(--tlk-blue-60)}.tlk-checkbox__tickbox:after{width:36px;height:36px;opacity:0;position:absolute;transition:opacity .2s ease-out,background-color .1s ease-out;display:block;content:\"\";top:calc(50% - 18px);left:calc(50% - 18px);border-radius:calc(var(--tlk-main-unit) * 2);border:9px solid var(--tlk-blue-60)}:host:not(.tlk-checkbox_disabled):hover ::ng-deep .tlk-checkbox__tickbox:after{opacity:.2;transition:opacity .2s ease-in,background-color .1s ease-in}.tlk-checkbox__tick{position:absolute;right:6.5px;top:4.0251262659px;z-index:1;display:inline-block;transform:rotate(45deg);width:7px;height:15px;border-bottom:3px solid;border-right:3px solid;border-color:transparent}:host.tlk-checkbox_checked ::ng-deep .tlk-checkbox__tick{border-color:var(--tlk-blue-80)}:host.tlk-checkbox_indeterminate ::ng-deep .tlk-checkbox__tick{transition:none;transform:none;border-right:none;border-bottom:4px solid var(--tlk-blue-80);height:4px;width:8px;right:6px;top:12px}:host.tlk-checkbox_checked.tlk-checkbox_disabled ::ng-deep .tlk-checkbox__tick{border-color:var(--tlk-gray-50)}:host.tlk-checkbox_indeterminate.tlk-checkbox_disabled ::ng-deep .tlk-checkbox__tick{border-color:var(--tlk-gray-50)}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], checked: [{
                type: Input
            }], indeterminate: [{
                type: Input
            }], changeEvent: [{
                type: Output
            }], indeterminateChanged: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { TlkCheckboxComponent };
